import React from 'react';
import { TrackedLink as Link } from 'components/common/TrackedLink';
import { Section } from './Section';
import { StyleConstant } from 'util/getStyleConstant';
import styled from 'styled-components';
import {
  CaseSidebarHeading,
  caseHeadingsAtom,
} from 'pages/cases/case/CaseContentDocument';
import { useAtomValue } from 'jotai';
import { useScrollSyncCurrentId } from 'features/ScrollSync';

export interface NavOutlineProps {
  title: string;
  icon: string;
  href?: string;
}

function NavHeading({
  heading: { headingId, title, level, element },
}: {
  heading: CaseSidebarHeading;
}) {
  const currentId = useScrollSyncCurrentId();
  return (
    <Section
      id={headingId!}
      title={title!}
      level={level}
      element={element}
      current={currentId === headingId}
    />
  );
}

export const NavOutline = ({ title, icon, href }: NavOutlineProps) => {
  const headings = useAtomValue(caseHeadingsAtom);

  return (
    <StyledNavOutline>
      <header>
        <h3>
          {icon && <i className={`fas fa-${icon}`} />}
          {href ? <Link to={href}>{title}</Link> : title}
        </h3>
      </header>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <ol tabIndex={0} aria-label={title}>
        {headings.map((heading, i) => (
          <NavHeading key={`${heading.headingId}-${i}`} heading={heading} />
        ))}
      </ol>
    </StyledNavOutline>
  );
};

const StyledNavOutline = styled.div`
  @media screen and (min-width: ${StyleConstant.BREAKPOINT_HANDHELD}) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    height: 100%;
    max-width: 360px;
    min-width: 360px;

    > header {
      flex: 0 1 auto;
    }
    > ol {
      flex: 1 1 auto;
      overflow-y: auto;
      overflow-x: hidden;
    }
  }

  > header {
    padding: 16px 22px 12px;
    h3 {
      font-family: var(--FONT_SERIF);
      font-size: 19px;
      button,
      i {
        font-size: 18px;
        margin-right: 0.5em;
      }
      button i {
        margin-right: 0;
      }
    }
  }

  > ol {
    list-style: none;
    margin: 0;
    overflow: auto;
    outline: 0;
    ol {
      list-style: none;
      margin: 0;
    }
    li {
      margin: 0;
      padding: 0;
    }
  }
  a,
  a:visited {
    color: inherit;
  }
  @media only print {
    display: none;
  }
`;
